import { Divider, Grid, Select, Text, TextInput } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form/lib/use-form";
import { useEffect, useState } from "react";
import { cities, provinces } from "../../../../data/province-city.array";
import { Cities } from "../../../../type/province-city.types";
import { UpdateMerchantOnboardingFormValues } from "../../../../type/update-merchant-onboarding-form-values.types";
import PhoneCountryCodeIcon from "../../../icons/phone-country-code-icon.component";

interface UpdateMainOfficeContactInfoProps {
  form: UseFormReturnType<UpdateMerchantOnboardingFormValues>;
}
function UpdateMainOfficeContactInfo(props: UpdateMainOfficeContactInfoProps) {
  const { form } = props;
  const [newProvinces, setNewProvinces] = useState(provinces);
  const [newCities, setNewCities] = useState(cities);

  useEffect(() => {
    if (
      !provinces.some((province: { value: string; label: string }) => {
        if (province.value === form.values.mainOfficeProvince) {
          return true;
        }
        return false;
      })
    ) {
      setNewProvinces([
        ...newProvinces,
        {
          key: form.values.mainOfficeProvince,
          value: form.values.mainOfficeProvince,
          label: form.values.mainOfficeProvince,
        },
      ]);
      const newProvince = {
        [form.values.mainOfficeProvince]: [
          {
            key: form.values.mainOfficeCity,
            value: form.values.mainOfficeCity,
            label: form.values.mainOfficeCity,
          },
        ],
      };
      const newCities: Cities = {
        ...cities,
        ...newProvince,
      };
      setNewCities(newCities);
    } else if (
      !cities[form.values.mainOfficeProvince].some(
        (city: { value: string; label: string }) => {
          if (city.value === form.values.mainOfficeCity) {
            return true;
          }
          return false;
        }
      )
    ) {
      setNewCities({
        ...newCities,
        [form.values.mainOfficeProvince]: [
          ...newCities[form.values.mainOfficeProvince],
          {
            key: form.values.mainOfficeCity,
            value: form.values.mainOfficeCity,
            label: form.values.mainOfficeCity,
          },
        ],
      });
    }
  }, []);

  const label = {
    mainOfficeContactNumber: "Main office contact number",
    mainOfficeBldgNameNo: "Main office building name and number",
    mainOfficeStreetAddress: "Main office street address",
    mainOfficeBarangay: "Main office barangay",
    mainOfficeCity: "Main office city",
    mainOfficeProvince: "Main office province",
    mainOfficePostalCode: "Main office postal code",
  };
  const placeHolder = {
    mainOfficeBldgNameNo: "Building name and number",
    mainOfficeStreetAddress: "Street address",
    mainOfficeBarangay: "Barangay",
    mainOfficeCity: "City",
    mainOfficeProvince: "Province",
    mainOfficePostalCode: "Postal code",
  };

  return (
    <>
      <Grid data-cy="mainContactInfo">
        <Grid.Col>
          <Text size="xl" weight={700}>
            Main Office Contact Information
          </Text>
        </Grid.Col>
        <Grid.Col>
          <TextInput
            label={label.mainOfficeContactNumber}
            value={form.values.mainOfficeContactNumber}
            icon={<PhoneCountryCodeIcon />}
            iconWidth={48}
            onChange={(event) => {
              form.setFieldValue(
                "mainOfficeContactNumber",
                event.currentTarget.value
              );
            }}
            error={form.errors.mainOfficeContactNumber}
            data-cy="contactNumber"
          />
        </Grid.Col>
        <Grid.Col>
          <TextInput
            placeholder={placeHolder.mainOfficeBldgNameNo}
            label={label.mainOfficeBldgNameNo}
            value={form.values.mainOfficeBldgNameNo}
            onChange={(event) => {
              form.setFieldValue(
                "mainOfficeBldgNameNo",
                event.currentTarget.value
              );
            }}
            error={form.errors.mainOfficeBldgNameNo}
            data-cy="bldgNameNo"
          />
        </Grid.Col>
        <Grid.Col>
          <TextInput
            placeholder={placeHolder.mainOfficeStreetAddress}
            label={label.mainOfficeStreetAddress}
            value={form.values.mainOfficeStreetAddress}
            onChange={(event) => {
              form.setFieldValue(
                "mainOfficeStreetAddress",
                event.currentTarget.value
              );
            }}
            error={form.errors.mainOfficeStreetAddress}
            data-cy="streetAddress"
          />
        </Grid.Col>
        <Grid.Col xs={12} sm={6}>
          <Select
            label={label.mainOfficeProvince}
            placeholder={placeHolder.mainOfficeProvince}
            data={newProvinces}
            value={form.values.mainOfficeProvince}
            onChange={(value: string) => {
              form.setFieldValue("mainOfficeProvince", value);
              form.setFieldValue("mainOfficeCity", "");
            }}
            searchable
            nothingFound="No options"
            error={form.errors.mainOfficeProvince}
            data-cy="province"
          />
        </Grid.Col>
        <Grid.Col xs={12} sm={6}>
          <Select
            label={label.mainOfficeCity}
            placeholder={placeHolder.mainOfficeCity}
            data={
              form.values.mainOfficeProvince.length > 0 &&
              Object.keys(newCities).includes(form.values.mainOfficeProvince)
                ? newCities[form.values.mainOfficeProvince]
                : []
            }
            disabled={form.values.mainOfficeProvince === ""}
            searchable
            nothingFound="No options"
            value={form.values.mainOfficeCity}
            onChange={(value: string) => {
              form.setFieldValue("mainOfficeCity", value);
            }}
            error={form.errors.mainOfficeCity}
            data-cy="city"
          />
        </Grid.Col>
        <Grid.Col xs={12} md={6}>
          <TextInput
            placeholder={placeHolder.mainOfficeBarangay}
            label={label.mainOfficeBarangay}
            value={form.values.mainOfficeBarangay}
            onChange={(event) => {
              form.setFieldValue(
                "mainOfficeBarangay",
                event.currentTarget.value
              );
            }}
            error={form.errors.mainOfficeBarangay}
            data-cy="barangay"
          />
        </Grid.Col>
        <Grid.Col xs={12} md={6}>
          <TextInput
            placeholder={placeHolder.mainOfficePostalCode}
            label={label.mainOfficePostalCode}
            value={form.values.mainOfficePostalCode}
            onChange={(event) => {
              form.setFieldValue(
                "mainOfficePostalCode",
                event.currentTarget.value
              );
            }}
            error={form.errors.mainOfficePostalCode}
            data-cy="postalCode"
          />
        </Grid.Col>
        <Grid.Col>
          <Divider my="md" />
        </Grid.Col>
      </Grid>
    </>
  );
}

export default UpdateMainOfficeContactInfo;
